import {
  Activity,
  BellRing,
  Building2,
  Car,
  Cctv,
  ConciergeBell,
  DoorOpen,
  History,
  LayoutDashboard,
  MailPlus,
  Megaphone,
  Package,
  ParkingSquare,
  PhoneCall,
  Router,
  ScrollText,
  Settings2,
  ShieldCheck,
  Siren,
  UserCheck,
  Users,
  Warehouse,
  type LucideIcon,
  Wallet,
} from 'lucide-react';
import { type CurrentUser, type Permission, REGISTRY_READ_ROLES } from '@brimostech/shared';
import { moduleEnabled, blocksEnabled } from '@/lib/modules';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  visible: (user: CurrentUser) => boolean;
}

export interface NavGroup {
  label: string | null;
  items: NavItem[];
}

export function canReadRegistry(user: CurrentUser): boolean {
  return (
    user.isSuperadmin || (user.activeRole !== null && REGISTRY_READ_ROLES.includes(user.activeRole))
  );
}

const can =
  (...permissions: Permission[]) =>
  (user: CurrentUser) =>
    user.isSuperadmin || permissions.some((p) => user.permissions.includes(p));

export const NAV_GROUPS: NavGroup[] = [
  {
    label: null,
    items: [{ to: '/', label: 'Início', icon: LayoutDashboard, end: true, visible: () => true }],
  },
  {
    label: 'Cadastro',
    items: [
      { to: '/unidades', label: 'Unidades', icon: Building2, visible: can('registry:read') },
      {
        to: '/blocos',
        label: 'Blocos',
        icon: Warehouse,
        // Hidden for condominiums without blocks (Condomínio → Estrutura, ADR-098).
        visible: (user) => can('registry:write')(user) && blocksEnabled(user),
      },
      { to: '/pessoas', label: 'Pessoas', icon: Users, visible: can('registry:read') },
      { to: '/veiculos', label: 'Veículos', icon: Car, visible: can('registry:read') },
      { to: '/vagas', label: 'Vagas', icon: ParkingSquare, visible: can('registry:read') },
      {
        to: '/convites',
        label: 'Convites',
        icon: MailPlus,
        visible: can('invites:manage', 'users:read'),
      },
    ],
  },
  {
    label: 'Acesso',
    items: [
      {
        to: '/pontos-de-acesso',
        label: 'Pontos de acesso',
        icon: DoorOpen,
        visible: can('devices:read'),
      },
      { to: '/eventos', label: 'Eventos', icon: Activity, visible: can('events:read') },
      {
        to: '/regras-de-acesso',
        label: 'Regras de acesso',
        icon: ScrollText,
        visible: can('devices:read'),
      },
      { to: '/cameras', label: 'Câmeras', icon: Cctv, visible: can('devices:read') },
      { to: '/interfone', label: 'Interfone', icon: PhoneCall, visible: can('devices:read') },
      { to: '/portaria', label: 'Portaria', icon: ConciergeBell, visible: can('frontdesk:use') },
      {
        to: '/visitantes',
        label: 'Visitantes',
        icon: UserCheck,
        // Optional module: hidden until the condominium turns it on (Condomínio → Módulos).
        visible: (user) => can('visitors:read')(user) && moduleEnabled(user, 'visitors'),
      },
      {
        to: '/avisos',
        label: 'Avisos',
        icon: Megaphone,
        visible: (user) => can('announcements:read')(user) && moduleEnabled(user, 'announcements'),
      },
      {
        to: '/encomendas',
        label: 'Encomendas',
        icon: Package,
        visible: (user) => can('packages:read')(user) && moduleEnabled(user, 'packages'),
      },
      { to: '/alertas', label: 'Alertas', icon: BellRing, visible: can('alerts:read') },
    ],
  },
  {
    label: 'Administração',
    items: [
      {
        to: '/financeiro',
        label: 'Financeiro',
        icon: Wallet,
        visible: (user) => can('finance:read')(user) && moduleEnabled(user, 'finance'),
      },
      {
        to: '/condominio',
        label: 'Condomínio',
        icon: Settings2,
        visible: can('condominium:settings'),
      },
      { to: '/auditoria', label: 'Auditoria', icon: History, visible: can('audit:read') },
    ],
  },
  {
    label: 'Operador',
    items: [
      {
        to: '/operador',
        label: 'Plataforma',
        icon: ShieldCheck,
        end: true,
        visible: (user) => user.isSuperadmin,
      },
      {
        to: '/operador/gateways',
        label: 'Gateways',
        icon: Router,
        visible: (user) => user.isSuperadmin,
      },
      {
        to: '/operador/alertas',
        label: 'Alertas da frota',
        icon: Siren,
        visible: (user) => user.isSuperadmin,
      },
      {
        to: '/operador/releases',
        label: 'Releases do agente',
        icon: Package,
        visible: (user) => user.isSuperadmin,
      },
    ],
  },
];

export function visibleNavGroups(user: CurrentUser): NavGroup[] {
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => item.visible(user)),
  })).filter((group) => group.items.length > 0);
}

export function visibleNavItems(user: CurrentUser): NavItem[] {
  return visibleNavGroups(user).flatMap((g) => g.items);
}
